import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useDeferredValue, useEffect, useMemo, useState } from 'react';

import { getRunSupplierBns } from '@/api/findings';
import type { FindingOut, RuleOut } from '@/api/types';
import { useAuth } from '@/auth/AuthContext';
import { FindingFilterBar, type BnFilter } from '@/components/FindingFilterBar';
import { Badge } from '@/components/ui/badge';
import { useReturnToRow } from '@/hooks/useReturnToRow';
import { type StatusFilter } from '@/hooks/useFindingsReview';
import {
  lastVisitedSupplierKey,
  readStoredSupplierSort,
  supplierSortStorageKey,
  writeLastVisitedRow,
  writeStoredSupplierSort,
} from '@/lib/findingFilterStorage';
import { findingMatchesSearch } from '@/lib/findingSearch';
import { formatCurrency } from '@/lib/format';
import { cn } from '@/lib/utils';

function stringOrNull(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  return value.length > 0 ? value : null;
}

// ── Suppliers tab ────────────────────────────────────────────────────

type SupplierSortKey =
  | 'totalRecovery'
  | 'findingCount'
  | 'approvedCount'
  | 'pendingCount'
  | 'name';

const SUPPLIER_SORT_KEYS: readonly SupplierSortKey[] = [
  'totalRecovery',
  'findingCount',
  'approvedCount',
  'pendingCount',
  'name',
];

/** Narrow a persisted sort string to a valid key, or null if it's stale. */
function coerceSupplierSortKey(value: string | null): SupplierSortKey | null {
  return SUPPLIER_SORT_KEYS.includes(value as SupplierSortKey)
    ? (value as SupplierSortKey)
    : null;
}


interface SupplierAggregate {
  name: string;
  findingCount: number;
  ruleCount: number;
  pending: number;
  approved: number;
  rejected: number;
  needsReview: number;
  reviewed: number;
  totalRecovery: number;
  approvedRecovery: number;
  pendingRecovery: number;
  regulatoryClass: string | null;
  taxClass: string | null;
  province: string | null;
  supplierId: string | null;
}

/** Per-supplier rollup of a finding list. Used by the Suppliers tab to
 *  give Andrew + Ibrahim a bird's-eye view sorted by recovery impact. */
function aggregateBySupplier(findings: FindingOut[]): SupplierAggregate[] {
  const map = new Map<string, SupplierAggregate>();
  for (const f of findings) {
    const name = stringOrNull(f.evidence['supplier.name']) ?? '(unknown supplier)';
    let agg = map.get(name);
    if (!agg) {
      agg = {
        name,
        findingCount: 0,
        ruleCount: 0,
        pending: 0,
        approved: 0,
        rejected: 0,
        needsReview: 0,
        reviewed: 0,
        totalRecovery: 0,
        approvedRecovery: 0,
        pendingRecovery: 0,
        regulatoryClass: stringOrNull(f.evidence['supplier.regulatory_class']),
        taxClass: stringOrNull(f.evidence['supplier.tax_class']),
        province: stringOrNull(f.evidence['supplier.province']),
        supplierId: f.supplier_id,
      };
      map.set(name, agg);
    }
    // Capture classification once from any finding — same supplier always
    // has the same classification (set at ingest, immutable per run).
    if (agg.regulatoryClass === null) {
      agg.regulatoryClass = stringOrNull(f.evidence['supplier.regulatory_class']);
    }
    if (agg.taxClass === null) {
      agg.taxClass = stringOrNull(f.evidence['supplier.tax_class']);
    }
    if (agg.province === null) {
      agg.province = stringOrNull(f.evidence['supplier.province']);
    }
    if (agg.supplierId === null) {
      agg.supplierId = f.supplier_id;
    }
    agg.findingCount += 1;
    // Per-supplier rollup, single-currency assumption — see the
    // run-level total above. Honest for the mono-currency engagements
    // shipping today; mixed-currency needs grouping by currency.
    const amt = Number(f.recovery.amount);
    if (Number.isFinite(amt)) agg.totalRecovery += amt;
    if (f.status === 'pending') {
      agg.pending += 1;
      if (Number.isFinite(amt)) agg.pendingRecovery += amt;
    } else if (f.status === 'approved') {
      agg.approved += 1;
      if (Number.isFinite(amt)) agg.approvedRecovery += amt;
      agg.reviewed += 1;
    } else if (f.status === 'rejected') {
      agg.rejected += 1;
      agg.reviewed += 1;
    } else if (f.status === 'needs_review') {
      agg.needsReview += 1;
    }
  }
  // Second pass: count distinct rule ids per supplier.
  const rulesPerSupplier = new Map<string, Set<string>>();
  for (const f of findings) {
    const name = stringOrNull(f.evidence['supplier.name']) ?? '(unknown supplier)';
    let s = rulesPerSupplier.get(name);
    if (!s) {
      s = new Set();
      rulesPerSupplier.set(name, s);
    }
    s.add(f.rule_id);
  }
  for (const [name, ruleSet] of rulesPerSupplier) {
    const agg = map.get(name);
    if (agg) agg.ruleCount = ruleSet.size;
  }
  return Array.from(map.values());
}

export function SuppliersTab({
  findings,
  rules,
  runId,
}: {
  findings: FindingOut[];
  rules: RuleOut[];
  runId: string;
}) {
  const [ruleFilter, setRuleFilter] = useState<string>('');
  const [provinceFilter, setProvinceFilter] = useState<string>('');
  const [supplierFilter, setSupplierFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('');
  const [bnFilter, setBnFilter] = useState<BnFilter>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const deferredSearchQuery = useDeferredValue(searchQuery);
  // Persist the chosen sort per run so it survives navigating into a
  // supplier and back — same behaviour as the list view.
  const sortStorageKey = supplierSortStorageKey(runId);
  const [sortKey, setSortKey] = useState<SupplierSortKey>(
    () => coerceSupplierSortKey(readStoredSupplierSort(sortStorageKey)) ?? 'totalRecovery',
  );
  useEffect(() => {
    writeStoredSupplierSort(sortStorageKey, sortKey);
  }, [sortStorageKey, sortKey]);

  // BN column source: the curated `suppliers.bn` (keyed by supplier id),
  // NOT the finding evidence snapshot — `evidence['supplier.bn']` is a
  // near-always-null ingest read (BNs are captured later via the timeline
  // editor), so reading it left the column blank for everyone (INP-508).
  const { userKey, getIdToken } = useAuth();
  const bnQuery = useQuery({
    queryKey: ['run-supplier-bns', runId, userKey],
    queryFn: async () => getRunSupplierBns(runId, (await getIdToken()) ?? ''),
  });
  const bnBySupplierId = bnQuery.data?.bns;
  const bnFor = (agg: SupplierAggregate): string | null =>
    agg.supplierId ? bnBySupplierId?.[agg.supplierId] ?? null : null;

  const ruleIds = useMemo(
    () => Array.from(new Set(findings.map((f) => f.rule_id))).sort(),
    [findings],
  );
  const ruleById = useMemo(() => {
    const m = new Map<string, RuleOut>();
    for (const r of rules) m.set(r.id, r);
    return m;
  }, [rules]);
  const provinces = useMemo(() => {
    const set = new Set<string>();
    for (const f of findings) {
      const p = stringOrNull(f.evidence['supplier.province']);
      if (p) set.add(p);
    }
    return Array.from(set).sort();
  }, [findings]);
  const supplierNames = useMemo(() => {
    const set = new Set<string>();
    for (const f of findings) {
      const n = stringOrNull(f.evidence['supplier.name']);
      if (n) set.add(n);
    }
    return Array.from(set).sort();
  }, [findings]);

  // Filter pass at the FINDING level (Rule / Province / free-text search
  // narrow which findings count toward each supplier's totals), then
  // aggregate. The search runs pre-aggregation so a query like
  // "HST on Applicable Amounts" surfaces the suppliers whose findings
  // carry that memo.
  const filteredFindings = useMemo(() => {
    const q = deferredSearchQuery.trim().toLowerCase();
    return findings.filter((f) => {
      if (ruleFilter && f.rule_id !== ruleFilter) return false;
      if (
        provinceFilter &&
        stringOrNull(f.evidence['supplier.province']) !== provinceFilter
      )
        return false;
      if (q && !findingMatchesSearch(f, q)) return false;
      return true;
    });
  }, [findings, ruleFilter, provinceFilter, deferredSearchQuery]);

  const aggregates = useMemo(() => aggregateBySupplier(filteredFindings), [filteredFindings]);

  // Filter pass at the SUPPLIER level (Supplier text + Status + BN presence).
  const visible = useMemo(() => {
    let rows = aggregates;
    if (supplierFilter) {
      const needle = supplierFilter.toLowerCase();
      rows = rows.filter((s) => s.name.toLowerCase().includes(needle));
    }
    // Only narrow on BN once the map has loaded — while `bnBySupplierId`
    // is undefined (pending / errored) every supplier reads as BN-less, so
    // applying the filter would falsely match all of them under "Missing".
    if (bnBySupplierId) {
      if (bnFilter === 'has') {
        rows = rows.filter((s) => bnFor(s) !== null);
      } else if (bnFilter === 'missing') {
        rows = rows.filter((s) => bnFor(s) === null);
      }
    }
    if (statusFilter) {
      // Show suppliers with ≥1 finding in the chosen status.
      rows = rows.filter((s) => {
        if (statusFilter === 'pending') return s.pending > 0;
        if (statusFilter === 'approved') return s.approved > 0;
        if (statusFilter === 'rejected') return s.rejected > 0;
        if (statusFilter === 'needs_review') return s.needsReview > 0;
        return true;
      });
    }
    const sorted = [...rows];
    if (sortKey === 'totalRecovery') {
      sorted.sort((a, b) => b.totalRecovery - a.totalRecovery);
    } else if (sortKey === 'findingCount') {
      sorted.sort((a, b) => b.findingCount - a.findingCount);
    } else if (sortKey === 'approvedCount') {
      sorted.sort((a, b) => b.approved - a.approved);
    } else if (sortKey === 'pendingCount') {
      sorted.sort((a, b) => b.pending - a.pending);
    } else if (sortKey === 'name') {
      sorted.sort((a, b) => a.name.localeCompare(b.name));
    }
    return sorted;
  }, [aggregates, supplierFilter, statusFilter, bnFilter, bnBySupplierId, sortKey]);

  // On return from a supplier detail page, scroll the row we came from back
  // into view and highlight it. Supplier rows are keyed by name.
  const visibleNames = useMemo(() => visible.map((s) => s.name), [visible]);
  const { lastVisitedId, containerRef } = useReturnToRow(
    lastVisitedSupplierKey(runId),
    visibleNames,
  );

  const hasActiveFilter = !!(
    ruleFilter ||
    provinceFilter ||
    supplierFilter ||
    statusFilter ||
    bnFilter ||
    searchQuery.trim()
  );
  const clearAll = () => {
    setRuleFilter('');
    setProvinceFilter('');
    setSupplierFilter('');
    setStatusFilter('');
    setBnFilter('');
    setSearchQuery('');
  };

  return (
    <div className="grid gap-3">
      <FindingFilterBar
        ruleFilter={ruleFilter}
        setRuleFilter={setRuleFilter}
        provinceFilter={provinceFilter}
        setProvinceFilter={setProvinceFilter}
        supplierFilter={supplierFilter}
        setSupplierFilter={setSupplierFilter}
        transactionTypeFilter=""
        setTransactionTypeFilter={() => {}}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        bnFilter={bnFilter}
        setBnFilter={setBnFilter}
        bnLookupFailed={bnQuery.isError}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        ruleIds={ruleIds}
        ruleById={ruleById}
        provinces={provinces}
        suppliers={supplierNames}
        transactionTypes={[]}
        totalCount={aggregates.length}
        showingCount={visible.length}
        hasActiveFilter={hasActiveFilter}
        onClearAll={clearAll}
      />

      <div
        ref={containerRef}
        className="overflow-x-auto rounded-md border border-border"
      >
        <table className="w-full min-w-[1000px] text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-brand-slate">
            <tr>
              <SupplierSortHeader
                label="Supplier"
                sortKey="name"
                activeKey={sortKey}
                onSort={setSortKey}
              />
              <th className="px-3 py-2">BN</th>
              <SupplierSortHeader
                label="Findings"
                sortKey="findingCount"
                activeKey={sortKey}
                onSort={setSortKey}
              />
              <th className="px-3 py-2">Progress</th>
              <th className="px-3 py-2">Status mix</th>
              <SupplierSortHeader
                label="Total recovery"
                sortKey="totalRecovery"
                activeKey={sortKey}
                onSort={setSortKey}
                rightAlign
              />
              <SupplierSortHeader
                label="Approved"
                sortKey="approvedCount"
                activeKey={sortKey}
                onSort={setSortKey}
                rightAlign
              />
              <SupplierSortHeader
                label="Pending"
                sortKey="pendingCount"
                activeKey={sortKey}
                onSort={setSortKey}
                rightAlign
              />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {visible.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-brand-slate">
                  {findings.length === 0
                    ? 'No findings on this run yet.'
                    : 'No suppliers match the current filters.'}
                </td>
              </tr>
            ) : (
              visible.map((s) => (
                <SupplierRow
                  key={s.name}
                  agg={s}
                  bn={bnFor(s)}
                  runId={runId}
                  isLastVisited={s.name === lastVisitedId}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

    </div>
  );
}

function SupplierSortHeader({
  label,
  sortKey,
  activeKey,
  onSort,
  rightAlign,
}: {
  label: string;
  sortKey: SupplierSortKey;
  activeKey: SupplierSortKey;
  onSort: (key: SupplierSortKey) => void;
  rightAlign?: boolean;
}) {
  const active = activeKey === sortKey;
  return (
    <th className={cn('px-3 py-2', rightAlign && 'text-right')}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={cn(
          'inline-flex items-center gap-1 hover:text-brand-navy',
          active && 'text-brand-navy',
        )}
      >
        {label}
        {active && <span aria-hidden="true">↓</span>}
      </button>
    </th>
  );
}

function SupplierRow({
  agg,
  bn,
  runId,
  isLastVisited,
}: {
  agg: SupplierAggregate;
  /** Curated current BN (`suppliers.bn`) for this supplier, or null. */
  bn: string | null;
  runId: string;
  isLastVisited: boolean;
}) {
  const reviewedPct =
    agg.findingCount > 0 ? Math.round((agg.reviewed / agg.findingCount) * 100) : 0;
  // The row last opened gets a blue highlight on return, matching the
  // findings tables.
  const highlight = isLastVisited
    ? 'bg-brand-royal/10 ring-2 ring-inset ring-brand-royal/40'
    : '';
  return (
    <tr
      data-row-id={agg.name}
      className={cn('relative cursor-pointer hover:bg-muted/30', highlight)}
    >
      <td className="px-3 py-2">
        <div className="flex flex-wrap items-center gap-1.5">
          {/* Stretched link: the ::after overlay makes the whole row a real
              anchor — left-click navigates, right / cmd / middle-click open
              the supplier in a new tab. Route by the stable supplier id when
              present so a `/` in the name can't break the URL; fall back to
              the name for id-less suppliers (the "(unknown supplier)" bucket). */}
          {agg.supplierId ? (
            <Link
              to="/runs/$id/suppliers/by-id/$supplierId"
              params={{ id: runId, supplierId: agg.supplierId }}
              onClick={() => writeLastVisitedRow(lastVisitedSupplierKey(runId), agg.name)}
              className="font-medium text-brand-navy after:absolute after:inset-0 after:content-[''] hover:underline"
            >
              {agg.name}
            </Link>
          ) : (
            <Link
              to="/runs/$id/suppliers/$supplierName"
              params={{ id: runId, supplierName: agg.name }}
              onClick={() => writeLastVisitedRow(lastVisitedSupplierKey(runId), agg.name)}
              className="font-medium text-brand-navy after:absolute after:inset-0 after:content-[''] hover:underline"
            >
              {agg.name}
            </Link>
          )}
          {agg.regulatoryClass && agg.regulatoryClass !== 'none' && (
            <Badge variant="outline" className="border-brand-royal/40 bg-brand-royal/5 text-brand-royal">
              {agg.regulatoryClass}
            </Badge>
          )}
          {agg.province && (
            <Badge variant="outline" className="text-brand-slate">
              {agg.province}
            </Badge>
          )}
        </div>
      </td>
      <td className="px-3 py-2 whitespace-nowrap font-mono text-xs text-brand-slate">
        {bn ?? ''}
      </td>
      <td className="px-3 py-2 whitespace-nowrap text-brand-slate">
        {agg.findingCount} · {agg.ruleCount} {agg.ruleCount === 1 ? 'rule' : 'rules'}
      </td>
      <td className="px-3 py-2 whitespace-nowrap">
        <div className="flex items-center gap-2">
          <div className="h-1.5 w-20 overflow-hidden rounded-full bg-secondary">
            <div
              className="h-full bg-brand-royal"
              style={{ width: `${reviewedPct}%` }}
            />
          </div>
          <span className="text-xs text-brand-slate">
            {agg.reviewed} / {agg.findingCount}
          </span>
        </div>
      </td>
      <td className="px-3 py-2">
        <div className="flex flex-wrap items-center gap-1 text-xs">
          {agg.pending > 0 && (
            <span className="rounded bg-secondary px-1.5 py-0.5 text-brand-slate">
              {agg.pending} pending
            </span>
          )}
          {agg.approved > 0 && (
            <span className="rounded bg-green-50 px-1.5 py-0.5 text-green-800">
              {agg.approved} approved
            </span>
          )}
          {agg.rejected > 0 && (
            <span className="rounded bg-red-50 px-1.5 py-0.5 text-red-800">
              {agg.rejected} rejected
            </span>
          )}
          {agg.needsReview > 0 && (
            <span className="rounded bg-amber-50 px-1.5 py-0.5 text-amber-900">
              {agg.needsReview} needs review
            </span>
          )}
        </div>
      </td>
      <td className="px-3 py-2 text-right font-mono tabular-nums font-semibold text-brand-navy whitespace-nowrap">
        {formatCurrency(String(agg.totalRecovery.toFixed(2)))}
      </td>
      <td className="px-3 py-2 text-right font-mono tabular-nums text-brand-slate whitespace-nowrap">
        {formatCurrency(String(agg.approvedRecovery.toFixed(2)))}
      </td>
      <td className="px-3 py-2 text-right font-mono tabular-nums text-brand-slate whitespace-nowrap">
        {formatCurrency(String(agg.pendingRecovery.toFixed(2)))}
      </td>
    </tr>
  );
}
